import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Download, Network } from "lucide-react";

// DSS-branded UML set for every project — loads automatically (no generate step),
// derived from the project's own pages/forms. Each diagram downloads as SVG.
export default function UMLPanel({ projectId }) {
  const [diagrams, setDiagrams] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [open, setOpen] = useState("usecase");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${projectId}/uml`)
      .then(({ data }) => {
        setDiagrams(data.diagrams || []);
        setProjectName(data.project || "");
      })
      .catch(() => toast.error("Could not load UML diagrams"))
      .finally(() => setLoading(false));
  }, [projectId]);

  const download = (d) => {
    const blob = new Blob([d.svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${projectName.replace(/\s+/g, "-")}-${d.key}-uml.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/uml/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${projectName.replace(/\s+/g, "-")}-uml.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("UML PDF downloaded");
    } catch {
      toast.error("Could not build the UML PDF");
    }
  };

  const active = diagrams.find((d) => d.key === open);

  return (
    <div className="accent-bar premium-card p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="uml-panel">
      <div className="flex items-center gap-2 mb-1">
        <Network className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
        <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">UML Design — auto for this project</div>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
        Every project gets its own DSS-branded UML set by default, built from the project's actual pages, forms and features. Pick a diagram to view or download it.
      </p>

      {loading ? (
        <div className="mt-3 text-[11px] code-font text-cyan-700 dark:text-cyan-300 tracking-widest" data-testid="uml-loading">DRAWING UML…</div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="uml-tabs">
            {diagrams.map((d) => (
              <button
                key={d.key}
                onClick={() => setOpen(d.key)}
                className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors ${
                  open === d.key
                    ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-200"
                    : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-cyan-400"
                }`}
                data-testid={`uml-tab-${d.key}`}
              >
                {d.title}
              </button>
            ))}
          </div>

          {active && (
            <div className="mt-3" data-testid="uml-canvas">
              <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-white/[0.08] bg-[#0B0F19] flex justify-center">
                <div dangerouslySetInnerHTML={{ __html: active.svg }} className="max-w-full [&>svg]:max-w-full [&>svg]:h-auto" />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  onClick={() => download(active)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors"
                  data-testid="uml-download-button"
                >
                  <Download className="w-3.5 h-3.5" /> Download SVG
                </button>
                <button
                  onClick={downloadPdf}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-600 to-sky-600 dark:from-cyan-400 dark:to-sky-400 text-white dark:text-[#090D16] text-[11px] font-bold uppercase tracking-wider hover:from-cyan-700 hover:to-sky-700 transition-all"
                  data-testid="uml-pdf-button"
                >
                  <Download className="w-3.5 h-3.5" /> UML PDF (all 5)
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
